import type * as React from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";

import {
  USER_DETAIL_TAB_IDS,
  type UserDetailTabId,
} from "@/features/admin/components/UserDetailContent";
import { UserDetailShell } from "@/features/admin/components/UserDetailShell";

/**
 * Admin User detail — full-width inline destination.
 *
 * Wire path: ``/admin/users/$userId``. Sibling to ``users/index.tsx`` (list)
 * under ``_authed/_modal/admin`` so list + detail are peers — the previous
 * ``users.tsx`` parent-style mount swallowed the child route (no ``<Outlet/>``
 * in the list component), exactly like the org-detail history in
 * ``orgs/index.tsx``.
 *
 * URL contract
 * ------------
 * Active tab is persisted via ``?tab=<overview|sessions|mfa|traces|pii>`` so
 * deep-links work, browser back walks the tab history, and refresh keeps the
 * user on the same tab. ``validateSearch`` whitelists the known tab ids; an
 * unknown ``?tab=`` value coerces to ``"overview"``. Mirrors
 * ``orgs/$orgId.tsx``.
 */

interface UserDetailSearch {
  readonly tab: UserDetailTabId;
}

export const Route = createFileRoute("/_authed/_modal/admin/users/$userId")({
  component: AdminUserDetailRoute,
  validateSearch: (raw: Record<string, unknown>): UserDetailSearch => {
    const candidate = typeof raw.tab === "string" ? raw.tab : "";
    const tab = (USER_DETAIL_TAB_IDS as readonly string[]).includes(candidate)
      ? (candidate as UserDetailTabId)
      : "overview";
    return { tab };
  },
});

function AdminUserDetailRoute(): React.ReactElement {
  const { userId } = useParams({ from: "/_authed/_modal/admin/users/$userId" });
  return <UserDetailShell userId={userId} />;
}
